import React, { useMemo, useState } from 'react';
import Card from '../Card';
import StatTile from '../StatTile';
import { Tabs } from '../ui';
import {
  HBarChart, TimeChart, formatCost, formatCurrency, formatMs, formatNumber, formatPercent,
  PALETTE,
} from '../charts';

// The Overview's Voice section (Mark, 2026-10-16), under LLM requests: the speech models,
// text to speech and speech to text, on their own. A speech call is billed to the user's
// budget like an LLM call, its seconds of audio at the model's price per second on the
// Voice page. So Total Cost, the Spend cards and the rankings count it, the LLM cards
// leave it out, and this section shows it. Every cost is the one charged when the call was
// made (usage_logs.cost_usd): a price changed later does not re-price past calls.
// Data: GET /api/dashboard/voice, under the page's filters.

const KINDS = [
  { key: 't2s', label: 'Text to speech', short: 'T2S', icon: 'play', did: 'spoken' },
  { key: 's2t', label: 'Speech to text', short: 'S2T', icon: 'voice', did: 'transcribed' },
];

const MODEL_METRICS = [
  { value: 'cost', label: 'Cost' },
  { value: 'seconds', label: 'Audio' },
  { value: 'calls', label: 'Calls' },
  { value: 'latency', label: 'Latency' },
];

const TIME_METRICS = [
  { value: 'cost', label: 'Cost' },
  { value: 'seconds', label: 'Audio' },
];

const plural = (n, word) => `${formatNumber(n)} ${word}${n === 1 ? '' : 's'}`;

// Seconds of audio as a duration: "0.4s", "45s", "12m 05s", "3h 04m".
export function formatAudio(seconds) {
  const v = Math.max(0, Number(seconds) || 0);
  if (v < 10) return `${Number(v.toFixed(1))}s`;
  const s = Math.round(v);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
}

const EMPTY_KIND = { calls: 0, seconds: 0, cost_usd: 0 };

export default function VoiceUsage({ data, totalCost }) {
  const [metric, setMetric] = useState('cost');
  const [timeMetric, setTimeMetric] = useState('cost');
  const byModel = useMemo(() => data?.by_model || [], [data]);
  const totals = KINDS.map(k => ({ ...k, ...EMPTY_KIND, ...(data?.totals?.[k.key] || {}) }));
  const calls = totals.reduce((s, t) => s + (t.calls || 0), 0);
  const seconds = totals.reduce((s, t) => s + (t.seconds || 0), 0);
  const cost = totals.reduce((s, t) => s + (t.cost_usd || 0), 0);
  const perMinute = seconds > 0 ? cost / (seconds / 60) : 0;
  // Total Cost counts the voice cost too, so with no cost at all there is no voice cost.
  const share = totalCost > 0 ? Math.min(100, (cost / totalCost) * 100) : 0;
  const empty = calls === 0 ? 'No speech calls in this window' : null;

  // One colour per model, most expensive first, so a model's bar and its line over time
  // are the same colour whichever metric either card shows.
  const modelColor = useMemo(() => {
    const out = {};
    [...byModel]
      .sort((a, b) => (b.cost_usd || 0) - (a.cost_usd || 0) || (b.calls || 0) - (a.calls || 0)
        || String(a.model).localeCompare(String(b.model)))
      .forEach((m) => {
        if (!(m.model in out)) out[m.model] = PALETTE[Object.keys(out).length % PALETTE.length];
      });
    return out;
  }, [byModel]);

  const bars = useMemo(() => byModel
    .map((m) => {
      const kind = KINDS.find(k => k.key === m.kind) || KINDS[0];
      const value = metric === 'cost' ? m.cost_usd
        : metric === 'seconds' ? m.seconds
        : metric === 'calls' ? m.calls
        : (m.avg_latency_ms || 0);
      return {
        key: `${m.model}:${m.kind}`, name: `${m.model} · ${kind.short}`, model: m.model,
        count: value || 0, calls: m.calls || 0, seconds: m.seconds || 0,
      };
    })
    .filter(r => r.count > 0)
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)), [byModel, metric]);

  const barValue = (v, row) => {
    if (metric === 'cost') return `${formatCost(v)} · ${plural(row.calls, 'call')}`;
    if (metric === 'seconds') return `${formatAudio(v)} · ${plural(row.calls, 'call')}`;
    if (metric === 'calls') return `${formatNumber(v)} · ${formatAudio(row.seconds)}`;
    return `${formatMs(v)} a call`;
  };
  // Calls at no cost leave the cost chart empty: say why, rather than "no data".
  const barsEmpty = metric === 'cost'
    ? 'These calls cost nothing: no speech model called has a price per second'
    : metric === 'latency' ? 'No latency recorded for these calls' : 'No speech calls in this window';

  const models = data?.models || [];
  const overTime = (timeMetric === 'cost' ? data?.data : data?.seconds) || [];
  const formatTime = timeMetric === 'cost' ? formatCost : formatAudio;

  return (
    <>
      <div className="section-head">
        <h2>Voice</h2>
        <p>what the speech models cost, and the audio they spoke and transcribed</p>
      </div>
      <div className="dashboard-grid">
        {totals.map(t => (
          <StatTile
            key={t.key}
            span={3}
            icon={t.icon}
            label={t.label}
            value={formatCost(t.cost_usd)}
            sub={t.calls > 0 ? `${plural(t.calls, 'call')} · ${formatAudio(t.seconds)} ${t.did}` : 'no calls'}
            help={t.key === 't2s'
              ? "What the agents' voice replies cost in this window: each call's seconds of audio times its model's price per second on the Voice page, charged to the user's budget when the call was made. The model is the one the Tools page names for the reply's language."
              : "What transcribing the users' voice messages cost in this window: each message's seconds of audio times the price per second of the speech to text model the Tools page picks, charged to the user's budget when it was transcribed."}
          />
        ))}
        <StatTile
          span={3}
          icon="clock"
          label="Audio"
          value={formatAudio(seconds)}
          sub={seconds > 0 ? `${formatCost(perMinute)} a minute, both kinds` : 'no speech calls'}
          help="Seconds of audio the speech models spoke and transcribed in this window. The cost a minute is the voice cost divided by those minutes, both kinds together."
        />
        <StatTile
          span={3}
          icon="analytics"
          label="Share of spend"
          value={formatPercent(share, share > 0 && share < 1 ? 2 : 1)}
          sub={totalCost > 0 ? `of ${formatCurrency(totalCost)} total cost` : 'no cost in this window'}
          help="The voice cost as a share of Total Cost, which counts LLM requests and speech calls alike, as the users' budgets do."
        />
        <Card title="Speech models"
              subtitle={`${MODEL_METRICS.find(m => m.value === metric).label.toLowerCase()} per model, both kinds`}
              help="Each speech model's calls in this window, text to speech (T2S) and speech to text (S2T). Latency is the mean time a call took, from the request to the audio or the text; days older than the Activity logs window have none."
              span={6}
              empty={empty}>
          <div className="tile-toolbar">
            <Tabs size="sm" label="Show speech models by" value={metric} onChange={setMetric}
                  tabs={MODEL_METRICS} />
          </div>
          <HBarChart
            data={bars}
            nameWidth={150}
            valueWidth={124}
            formatVal={barValue}
            colorFn={(row) => modelColor[row.model]}
            emptyText={barsEmpty}
          />
        </Card>
        <Card title="Voice over time"
              subtitle={`${timeMetric === 'cost' ? 'cost' : 'audio'} per model`}
              span={6}
              empty={empty}>
          <div className="tile-toolbar">
            <Tabs size="sm" label="Show voice over time by" value={timeMetric}
                  onChange={setTimeMetric} tabs={TIME_METRICS} />
          </div>
          <TimeChart
            data={overTime}
            labelKey="date"
            lines={models.map(m => ({ key: m, label: m }))}
            colors={models.map(m => modelColor[m] || PALETTE[0])}
            height={220}
            legend
            formatY={formatTime}
            formatValue={formatTime}
            scaleFloor={0}
          />
        </Card>
      </div>
    </>
  );
}
